import { useState } from "react";
import type { ArchiveEntry, FollowUp } from "../types";
import { createFollowUp, saveCaseVideo } from "../lib/data";
import { mediaTeam, type TeamMemberKey } from "../lib/team";

export function FollowUpEditor({
  entry,
  followUp,
  userId,
  onClose,
  onSaved,
}: {
  entry: ArchiveEntry;
  followUp?: FollowUp;
  userId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [assignedTo, setAssignedTo] = useState<TeamMemberKey | "">((followUp?.assigned_person as TeamMemberKey) || "");
  const [instructions, setInstructions] = useState(followUp?.instructions || "");
  const [priority, setPriority] = useState<"normal" | "high" | "urgent">(followUp?.priority || "normal");
  const [dueAt, setDueAt] = useState(followUp?.due_at?.slice(0, 10) || "");
  const [intervalDays, setIntervalDays] = useState<number>(followUp?.reminder_interval_days ?? 5);
  // Video decision lives on the CASE (archive entry) so it syncs two-way with the
  // desktop, not on the follow-up.
  const initialEditor = (entry.video_editor as TeamMemberKey) || "";
  const [makeVideo, setMakeVideo] = useState<"" | "yes" | "no">(
    entry.make_video === true ? "yes" : entry.make_video === false ? "no" : "",
  );
  const [videoEditor, setVideoEditor] = useState<TeamMemberKey | "">(initialEditor);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!instructions.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const editor = makeVideo === "yes" ? (videoEditor || null) : null;
      const driveUrl = entry.drive_locations?.[0]?.web_view_link ?? null;
      await createFollowUp({
        entryId: entry.id,
        caseId: entry.case_id,
        entryName: entry.name,
        userId,
        assignedPerson: assignedTo || null,
        instructions: instructions.trim(),
        priority,
        dueAt: dueAt ? new Date(`${dueAt}T18:00:00`).toISOString() : null,
        reminderIntervalDays: Math.min(30, Math.max(1, Math.round(intervalDays) || 5)),
        driveUrl,
      });
      await saveCaseVideo({
        entryId: entry.id,
        caseId: entry.case_id,
        entryName: entry.name,
        makeVideo: makeVideo === "yes" ? true : makeVideo === "no" ? false : null,
        videoEditor: editor,
        driveUrl,
        // Ping the editor only when the assignment is new or changed.
        notify: !!editor && editor !== initialEditor,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section className="modal" onMouseDown={(event) => event.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        <div className="eyebrow">{followUp ? "EDIT FOLLOW-UP" : "NEW FOLLOW-UP"}</div>
        <h2>{entry.case_id ?? entry.name}</h2>
        <p className="muted">{entry.case_id ? entry.name : `${entry.year} archive entry`}</p>

        <label className="field">
          <span>Assign to <small className="muted">— who collects the media</small></span>
          <select value={assignedTo} onChange={(event) => setAssignedTo(event.target.value as TeamMemberKey | "")}>
            <option value="">All media team (default)</option>
            {mediaTeam.map((member) => (
              <option key={member.key} value={member.key}>Only {member.name}</option>
            ))}
          </select>
        </label>
        <div className="field-row">
          <label className="field">
            <span>Priority</span>
            <select value={priority} onChange={(event) => setPriority(event.target.value as typeof priority)}>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </label>
          <label className="field">
            <span>Due date</span>
            <input type="date" value={dueAt} onChange={(event) => setDueAt(event.target.value)} />
          </label>
        </div>
        <label className="field">
          <span>Remind every <small className="muted">— days between reminders (default 5)</small></span>
          <input
            type="number"
            min={1}
            max={30}
            value={intervalDays}
            onChange={(event) => setIntervalDays(Number(event.target.value))}
          />
        </label>
        <label className="field">
          <span>What does the team need to collect?</span>
          <textarea
            rows={4}
            placeholder="Example: Treatment update photos and one horizontal video."
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
          />
        </label>
        <div className="field-row">
          <label className="field">
            <span>Make a video?</span>
            <select value={makeVideo} onChange={(event) => setMakeVideo(event.target.value as "" | "yes" | "no")}>
              <option value="">— not decided</option>
              <option value="yes">Yes — making a video</option>
              <option value="no">No video</option>
            </select>
          </label>
          {makeVideo === "yes" && (
            <label className="field">
              <span>Video editor <small className="muted">— who edits it</small></span>
              <select value={videoEditor} onChange={(event) => setVideoEditor(event.target.value as TeamMemberKey | "")}>
                <option value="">Unassigned</option>
                {mediaTeam.map((member) => (
                  <option key={member.key} value={member.key}>{member.name}</option>
                ))}
              </select>
            </label>
          )}
        </div>
        {error && <div className="alert error">{error}</div>}
        <div className="modal-actions">
          <button className="button secondary" onClick={onClose}>Cancel</button>
          <button className="button primary" disabled={busy || !instructions.trim()} onClick={() => void save()}>
            {busy ? "Saving…" : followUp ? "Save changes" : "Start follow-up"}
          </button>
        </div>
      </section>
    </div>
  );
}
